import { HttpStatusCode } from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { ClipLoader } from 'react-spinners';
import useApi from '../../../../../utils/AxiosWrapper';
import { useScreenView } from '../../../../../utils/ScreenViewContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useUser } from '../../../../../utils/UserContext';
import { useNavigate } from 'react-router-dom';

import SubmitMileageAccordionTable from './widgets/asset-details/SubmitMileageAccordionTable';
import { AccentButton, NeutralButton } from '../../../../../shared/Buttons';
import {
  CatenaMileageNotice,
  handleMileageConflictResponse,
  readCatenaConnected,
} from './catenaMileage';

export interface MileageReportRequestsResponse {
  mileageReportRequests: MileageReportRequest[];
  pagination?: {
    totalCount: number;
    hasMore: boolean;
  };
}

export interface MileageReportRequestUpdate {
  snapshot_id: string;
  cumulative_odometer: number;
  truck_id: string;
}

export interface RecentMileageSnapshots {
  created_at: string;
  cumulative_odometer: number;
  day_interval: number;
  manually_completed_at: string | null;
  miles: number | null;
  requested_driver_id: string | null;
  snapshot_id: string | null;
  source: string | null;
  status: string | null;
  submitted_by_driver_id: string | null;
  submitted_by_type: string | null;
  truck_id: string | null;
  driver?: {
    first_name: string;
    last_name: string;
  };
}

export interface MileageReportRequest {
  pagination: {
    hasMore: boolean;
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  eld_id: string | null;
  mostRecentRequest: RecentMileageSnapshots | null;
  recentMileageSnapshots: RecentMileageSnapshots[];
  truck_display_name: string;
  truck_id: string;
  truck_make: string;
  truck_model: string;
  truck_year: number;
  vehicle_id_number: string;
  previous_odometer?: number | null;
  current_mileage?: number | null;
}

interface SubmitMileageProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
}

export const SubmitMileage = ({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
}: SubmitMileageProps) => {
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<{ assets: MileageReportRequestUpdate[] }>();
  const { screenView } = useScreenView();
  const { callProtectedApi } = useApi();
  const [loading, setLoading] = useState(false);
  const [editingRows, setEditingRows] = useState<Set<string>>(new Set());

  // Toggle editing mode for a specific truck
  const toggleEditing = useCallback((truckId: string) => {
    setEditingRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(truckId)) {
        newSet.delete(truckId);
      } else {
        newSet.add(truckId);
      }
      return newSet;
    });
  }, []);

  // Clear editing state for specific truck IDs (called on successful submission)
  const clearEditingForTrucks = useCallback((truckIds: string[]) => {
    setEditingRows((prev) => {
      const newSet = new Set(prev);
      truckIds.forEach((id) => newSet.delete(id));
      return newSet;
    });
  }, []);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [perPage, setPerPage] = useState(20);

  const { userId, userRecord } = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const watchedAssets = watch('assets') || [];

  const isAnyMileageFilled = watchedAssets.some(
    (a) =>
      typeof a?.cumulative_odometer === 'number' && a.cumulative_odometer > 0
  );

  const updateMileageReport = async (updates: MileageReportRequestUpdate[]) => {
    setLoading(true);

    try {
      const response = await callProtectedApi({
        url: `/electronicLoggingDevices/reports/${userRecord?.userType}/${userId}`,
        requestType: 'PATCH',
        body: { updates },
        errorMsg: 'Error occurred while updating mileage report requests.',
        // A rejected reading (below the previous one, or an implausible jump) is only
        // fixable if the driver sees why — the API's 400 says which and by how much.
        preferServerErrorMessage: true,
      });
      if (response?.status === HttpStatusCode.Ok) {
        toast.success('Mileage Successfully Updated');
        // Clear editing state for successfully submitted trucks
        const submittedTruckIds = updates.map((u) => u.truck_id);
        clearEditingForTrucks(submittedTruckIds);
        // Invalidate both mileage report requests and odometer history queries
        queryClient.invalidateQueries({ queryKey: ['mileageReportRequests'] });
        queryClient.invalidateQueries({ queryKey: ['odometerHistory'] });
      } else {
        handleMileageConflictResponse(response, queryClient);
      }
    } catch (e: any) {
      console.error(
        'Error occurred while updating mileage report requests: ',
        e.response
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (data: { assets: MileageReportRequestUpdate[] }) => {
    const filteredAssets = data.assets.filter(
      (item) =>
        typeof item.cumulative_odometer === 'number' &&
        !isNaN(item.cumulative_odometer) &&
        item.cumulative_odometer > 0
    );

    // Validate that no odometer reading is less than the previous odometer (or current_mileage as fallback)
    const invalidAssets = filteredAssets.filter((item) => {
      const assetData = mileageReportRequests.find(
        (req) => req.truck_id === item.truck_id
      );
      const minOdometer =
        assetData?.previous_odometer ?? assetData?.current_mileage;
      if (minOdometer != null && item.cumulative_odometer < minOdometer) {
        return true;
      }
      return false;
    });

    if (invalidAssets.length > 0) {
      const invalidAssetNames = invalidAssets
        .map((item) => {
          const assetData = mileageReportRequests.find(
            (req) => req.truck_id === item.truck_id
          );
          return assetData?.truck_display_name || 'Unknown';
        })
        .join(', ');

      toast.error(
        `Odometer reading cannot be less than the previous odometer for: ${invalidAssetNames}`
      );
      return;
    }

    updateMileageReport(filteredAssets);
  };

  useEffect(() => {
    setPage(1);
    setPageSize(20);
    setPerPage(20);
  }, []);

  const fetchMileageReports = useCallback(async () => {
    const response = await callProtectedApi(
      `/electronicLoggingDevices/trucks-with-mileage-reports/${userId}?page=${page}&pageSize=${pageSize}`,
      'get',
      {}
    );

    const raw = response?.data?.data ?? response?.data ?? response;
    return normalizeMileageResponse(raw);
  }, [userId, page, pageSize, callProtectedApi]);

  /** Normalize the new shape or legacy array */
  const normalizeMileageResponse = (
    raw: any
  ): {
    items: MileageReportRequest[];
    total: number;
    catenaConnected: boolean;
  } => {
    // Unwrap axios shapes
    const root = raw?.data?.data ?? raw?.data ?? raw;

    const trucks = Array.isArray(root?.trucks) ? root.trucks : [];

    const items: MileageReportRequest[] = trucks.map((t: any) => ({
      // you kept pagination inside the item type, so copy the top-level pagination in
      pagination: {
        hasMore: !!root?.pagination?.hasMore,
        page: Number(root?.pagination?.page) || 1,
        pageSize: Number(root?.pagination?.pageSize) || trucks.length || 0,
        totalCount: Number(root?.pagination?.totalCount) || trucks.length || 0,
        totalPages: Number(root?.pagination?.totalPages) || 1,
      },
      eld_id: t?.eld_id ?? null,
      recentMileageSnapshots: Array.isArray(t?.recentMileageSnapshots)
        ? t.recentMileageSnapshots
        : [],
      mostRecentRequest: t?.mostRecentRequest ?? null,
      truck_display_name: t?.truck_display_name ?? '',
      truck_id: t?.truck_id ?? '',
      truck_make: t?.truck_make ?? '',
      truck_model: t?.truck_model ?? '',
      truck_year: Number(t?.truck_year) || 0,
      vehicle_id_number: t?.vehicle_id_number ?? '',
      previous_odometer: t?.previous_odometer ?? null,
      current_mileage: t?.current_mileage ?? null,
    }));

    const total =
      Number(root?.pagination?.totalCount) ||
      (Array.isArray(items) ? items.length : 0);

    return { items, total, catenaConnected: readCatenaConnected(root) };
  };

  const { data: mileageQueryData, isLoading: loadingMileageReports } = useQuery(
    {
      queryKey: [
        'mileageReportRequests',
        userId ?? '',
        userRecord?.userType ?? '',
        { page, pageSize },
      ],
      queryFn: fetchMileageReports,
      enabled: !!userId,
      staleTime: 15 * 60 * 1000,
      gcTime: 15 * 60 * 1000,
    }
  );

  // Handlers
  const handlePerPageChange = (value: number) => {
    setPerPage(value);
    setPage(1);
    setPageSize(value);
  };

  const handlePageChange = (nextPage: number, nextPageSize: number) => {
    if (nextPageSize !== pageSize) setPageSize(nextPageSize);
    setPage(nextPage);
  };

  const mileageReportRequests = mileageQueryData?.items ?? [];
  const mileageReportRequestsCount = mileageQueryData?.total ?? 0;
  const catenaConnected = mileageQueryData?.catenaConnected ?? false;

  const FooterButtons = useCallback(() => {
    return (
      <div className="flex h-[80px] max-h-[80px] min-h-[80px] w-full flex-row items-center justify-end gap-x-[16px] border-t-[1px] border-[#EAECF0] align-middle">
        <NeutralButton
          onClick={() => navigate('/dashboard/assets')}
          addClass="rounded-full w-[250px] h-[48px]"
          disabled={loading}
        >
          Cancel
        </NeutralButton>
        <AccentButton
          addClass={`h-[48px] w-[250px] rounded-full`}
          disabled={loading || !isAnyMileageFilled || catenaConnected}
          submit={true}
        >
          {!loading ? (
            'Save Mileage'
          ) : (
            <ClipLoader
              color="#1D213B"
              loading={loading}
              size={20}
              aria-label="Loading Spinner"
              data-testid="loader"
              className="flex h-full w-full justify-center"
            />
          )}
        </AccentButton>
      </div>
    );
  }, [isAnyMileageFilled, loading, navigate, catenaConnected]);

  // Hoisted so the three viewport branches below can't drift apart.
  const catenaNotice = catenaConnected && !loadingMileageReports && (
    <CatenaMileageNotice />
  );

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex h-full w-[1148px] max-w-[1148px] flex-col space-y-4 overflow-x-hidden pt-[16px]"
          >
            {catenaNotice}
            {!loadingMileageReports ? (
              <SubmitMileageAccordionTable
                mileageReportRequests={mileageReportRequests}
                catenaConnected={catenaConnected}
                register={register}
                control={control}
                offset={(page - 1) * pageSize}
                limit={pageSize}
                perPage={perPage}
                mileageReportRequestsCount={mileageReportRequestsCount}
                isLoadingCount={false}
                handlePerPageChange={handlePerPageChange}
                handlePageChange={handlePageChange}
                errors={errors}
                editingRows={editingRows}
                onToggleEditing={toggleEditing}
              />
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={loadingMileageReports}
                size={50}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="mt-10 flex h-full w-full items-center justify-center"
              />
            )}
            <FooterButtons />
          </form>
        );
      case 'tablet':
        return (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex h-full w-full max-w-full flex-col space-y-4 overflow-x-hidden overflow-y-hidden px-4 py-[16px]"
          >
            {catenaNotice}
            {!loadingMileageReports ? (
              <SubmitMileageAccordionTable
                mileageReportRequests={mileageReportRequests}
                catenaConnected={catenaConnected}
                register={register}
                control={control}
                offset={(page - 1) * pageSize}
                limit={pageSize}
                perPage={perPage}
                mileageReportRequestsCount={mileageReportRequestsCount}
                isLoadingCount={false}
                handlePerPageChange={handlePerPageChange}
                handlePageChange={handlePageChange}
                errors={errors}
                editingRows={editingRows}
                onToggleEditing={toggleEditing}
              />
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={loadingMileageReports}
                size={60}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="mt-10 flex h-full w-full items-center justify-center"
              />
            )}
            <FooterButtons />
          </form>
        );
      case 'mobile':
        return (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex h-full w-full max-w-full flex-col space-y-4 overflow-x-hidden overflow-y-hidden px-2 py-[16px]"
          >
            {catenaNotice}
            {!loadingMileageReports ? (
              <SubmitMileageAccordionTable
                mileageReportRequests={mileageReportRequests}
                catenaConnected={catenaConnected}
                register={register}
                control={control}
                offset={(page - 1) * pageSize}
                limit={pageSize}
                perPage={perPage}
                mileageReportRequestsCount={mileageReportRequestsCount}
                isLoadingCount={false}
                handlePerPageChange={handlePerPageChange}
                handlePageChange={handlePageChange}
                errors={errors}
                editingRows={editingRows}
                onToggleEditing={toggleEditing}
              />
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={loadingMileageReports}
                size={60}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="mt-10 flex h-full w-full items-center justify-center"
              />
            )}
            <FooterButtons />
          </form>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
};

export default SubmitMileage;
